import { useMemo, useState } from "react";
import { IUpdateSenderProfileRequest, SenderProfileAssigneeRecipient } from "shared";
import { When } from "react-if";
import ReplyIcon from '@mui/icons-material/Reply';
import { Tooltip } from "@mui/material";
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import OutgoingMailIcon from '@mui/icons-material/OutgoingMail';
import FaxIcon from '@mui/icons-material/Fax';
import ImageIcon from '@mui/icons-material/Image';
import HideImageIcon from '@mui/icons-material/HideImage';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from "react-toastify";

import { InputTextAddon } from "../../../../../../common/Atoms/InputTextAddon";
import { fieldIsValid } from "../../../../../lib/helper";
import { useCustomDomain } from "../../../../../hooks/useCustomDomain";
import { useGetSelectedOrganisation } from "../../../../../hooks/useGetSelectedOrganisation";
import { useAllowedFeature } from "../../../../../hooks/useAllowedFeature";
import { useHasValidSubscription } from "../../../../../hooks/useHasValidSubscription";
import { useGeneralSettingsForm } from "../../../../../hooks/useGeneralSettingsForm";
import { Card } from "../../../../../../common/Atoms/Card";
import { Paragraph } from "../../../../../../common/Atoms/Typography/Paragraph";
import { Heading } from "../../../../../../common/Atoms/Typography/Heading";
import { Badge } from "../../../../../../common/Atoms/Badge";
import { Button } from "../../../../../../common/Atoms/Button";
import { Toggle } from "../../../../../../common/Atoms/Toggle";
import { Select } from "../../../../../../common/Atoms/Select";
import { useRemoveSenderProfileMutation } from "../../../../../services/api/settingsApi/settings";
import { FormLabel } from "../../../../../../common/Atoms/FormLabel";
import { FormDescription } from "../../../../../../common/Atoms/FormDescription";
import { FileUpload } from "../../Main/FileUpload";

import { EmailSignature } from "./EmailSignature";

const assigneeRecipientOptions = [
  { label: `Don't include`, value: `` },
  { label: `CC`, value: SenderProfileAssigneeRecipient.CC },
  { label: `BCC`, value: SenderProfileAssigneeRecipient.BCC },
];

interface SenderProfileProps {
  profile: IUpdateSenderProfileRequest;
  index: number;
}

export function SenderProfile({
  profile,
  index,
}: SenderProfileProps) {
  const { data: customDomain } = useCustomDomain();
  const currentOrg = useGetSelectedOrganisation();
  const allowedCustomDomain = useAllowedFeature(`customDomain`);
  const hasValidSub = useHasValidSubscription();
  const { formData, onChange, invalids, onSave, updateLoading } = useGeneralSettingsForm();
  const [removeSenderProfile, { isLoading: removeLoading }] = useRemoveSenderProfileMutation();
  const [isEditing, setIsEditing] = useState(false);

  function handleChangeProfiles(e) {
    const newProfile = {
      ...profile,
      [e.name]: e.value,
    };

    const newProfiles = formData.senderProfiles?.map(p => {
      if (p.id === profile.id) {
        return newProfile;
      }

      return p;
    }) || [];

    onChange(`senderProfiles`, newProfiles);
  }

  // Only show before the @
  const fromAddressDisplay = useMemo(() => {
    if (customDomain?.verified && customDomain?.domain) {
      return profile?.fromAddress;
    }

    return `no-reply`;
  }, [ customDomain, profile ]);

  const fromPostfix = useMemo(() => {
    if (customDomain?.verified && customDomain?.domain) {
      return `@${customDomain.domain}`;
    }

    return `@mail.paidnice.com`;
  }, [ customDomain ]);

  const paidniceSharedReplyAddress = `reply+<id>@inbound.paidnice.com`;
  const usePaidniceMailbox = !!profile.replyToPaidniceMailbox;

  async function onSaveAndClose() {
    await onSave();
    setIsEditing(false);
  }

  async function removeProfile() {
    if (!currentOrg) {
      return;
    }

    if (profile.default) {
      toast.error(`Cannot delete the default sender profile.`);

      return;
    }

    try {
      await removeSenderProfile({
        organisationId: currentOrg.id,
        profileId: profile.id,
      }).unwrap();

      setIsEditing(false);
    }
    catch (err) {
      toast.error(err?.data?.message || `Could not delete the sender profile. Please try again.`);
    }
  }

  // Editable pre-subscription so setup is complete before subscribing; backend ignores it until subscribed
  const canEditFrom = customDomain?.verified && (allowedCustomDomain || !hasValidSub);

  const fromHelp = customDomain?.verified ?
    `` :
    `A custom domain is required to change the "from" address`;

  if (isEditing) {
    return (
      <Card className={ `space-y-2` }>
        <InputTextAddon
          label={ `Profile Name` }
          value={ profile.name || `` }
          name={ `name` }
          onChange={ handleChangeProfiles }
          invalid={ fieldIsValid(`senderProfiles[${index}].name`, invalids) }
          subHeading={ `Not visible to customers.` }
        />

        <Toggle
          label={ `Send replies to Paidnice shared mailbox` }
          checked={ usePaidniceMailbox }
          name={ `replyToPaidniceMailbox` }
          onChange={ handleChangeProfiles }
          description={ `When enabled, replies are routed to your Paidnice shared mailbox address.` }
        />

        <When condition={ !usePaidniceMailbox }>
          <InputTextAddon
            label={ `Reply To Address (Required)` }
            value={ profile.replyToAddress || `` }
            name={ `replyToAddress` }
            onChange={ handleChangeProfiles }
            invalid={ fieldIsValid(`senderProfiles[${index}].replyToAddress`, invalids) }
            subHeading={ `This is the minimum required field we need to be able to send emails` }
          />
        </When>

        <InputTextAddon
          label={ `BCC Address` }
          value={ profile.bccAddress || `` }
          name={ `bccAddress` }
          onChange={ handleChangeProfiles }
          invalid={ fieldIsValid(`senderProfiles[${index}].bccAddress`, invalids) }
          helpIcon={ `Enter up to 3 addresses, separated by commas` }
        />

        <InputTextAddon
          label={ `CC Address` }
          value={ profile.ccAddress || `` }
          name={ `ccAddress` }
          onChange={ handleChangeProfiles }
          invalid={ fieldIsValid(`senderProfiles[${index}].ccAddress`, invalids) }
          helpIcon={ `Enter up to 3 addresses, separated by commas` }
        />

        <Select
          name={ `assigneeRecipient` }
          label={ `Include assignee` }
          description={ `Also send a copy to the invoice's assignee, or the contact's assignee when the invoice has none.` }
          options={ assigneeRecipientOptions }
          selected={ profile.assigneeRecipient || `` }
          onChange={ v => handleChangeProfiles({
            name: `assigneeRecipient`,
            value: v.value || null,
          }) }
        />

        <InputTextAddon
          label={ `From Address` }
          value={ fromAddressDisplay || `` }
          name={ `fromAddress` }
          onChange={ handleChangeProfiles }
          disabled={ !canEditFrom }
          invalid={ fieldIsValid(`senderProfiles[${index}].fromAddress`, invalids) || fieldIsValid(`senderProfiles[${index}].fromName`, invalids) }
          description={ fromHelp }
          prefix={ profile.fromName || `` }
          addOnText={ fromPostfix }
          prefixOnChange={ prefix => handleChangeProfiles({
            name: `fromName`,
            value: prefix,
          }) }
        />

        <Toggle
          label={ `Send without a logo` }
          checked={ !!profile.hideLogo }
          name={ `hideLogo` }
          onChange={ handleChangeProfiles }
          description={ `Emails sent from this profile will not include a logo.` }
        />

        <When condition={ !profile.hideLogo }>
          <div>
            <FormLabel name={ `senderProfiles[${index}].logoUrl` }>{ `Logo Override` }</FormLabel>
            <FormDescription>
              { `Optional. Replaces the organisation logo on emails sent from this profile.` }
            </FormDescription>
            <FileUpload
              id={ `sender-profile-logo-${profile.id}` }
              url={ profile.logoUrl || undefined }
              setUrl={ v => handleChangeProfiles({
                name: `logoUrl`,
                value: v,
              }) }
              invalid={ fieldIsValid(`senderProfiles[${index}].logoUrl`, invalids) }
            />
          </div>
        </When>

        <EmailSignature
          profile={ profile }
        />

        <div className={ `flex justify-end` }>
          <When condition={ !profile.default }>
            <Button
              onClick={ removeProfile }
              color={ `red` }
              className={ `mr-2` }
              disabled={ removeLoading }
            >
              { `Delete Profile` }
            </Button>
          </When>
          <Button
            onClick={ onSaveAndClose }
            loading={ updateLoading }
          >
            { `Save Profile` }
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className={ `flex justify-between items-center` }>
        <div>
          { /* Name and badge */ }
          <div className={ `flex items-center mb-2` }>
            <a
              href={ `#` }
              onClick={ e => {
                e.preventDefault();
                setIsEditing(true);
              } }
            >
              <Heading
                variant={ `link` }
              >
                { profile.name }
              </Heading>
            </a>
            <When condition={ profile.default }>
              <Badge
                className={ `max-h-fit max-w-fit ml-2` }
                color={ `blue` }
                size={ `s` }
              >
                { `Default` }
              </Badge>
            </When>
          </div>

          { /* Fields */ }
          <div className={ `grid grid-cols-2 gap-2` }>
            <div>
              <Tooltip title={ `From Address` }>
                <span>
                  <Paragraph
                    as={ `span` }
                    className={ `flex items-center` }
                    variant={ `secondary` }
                  >
                    <ContactMailIcon
                      className={ `mr-1.5` }
                      fontSize={ `small` }
                    />
                    { profile.fromName } &lt;{ fromAddressDisplay }{ fromPostfix }&gt;

                  </Paragraph>
                </span>
              </Tooltip>
            </div>

            <div>
              <Tooltip title={ `Reply To Address` }>
                <span>
                  <Paragraph
                    as={ `span` }
                    className={ `flex items-center` }
                    variant={ `secondary` }
                  >
                    <ReplyIcon
                      className={ `mr-1.5` }
                      fontSize={ `small` }
                    />
                    { usePaidniceMailbox ? paidniceSharedReplyAddress : profile.replyToAddress }

                  </Paragraph>
                </span>
              </Tooltip>
            </div>

            <When condition={ !!profile.bccAddress }>
              { () => (
                <div>
                  <Tooltip title={ `BCC Address` }>
                    <span>
                      <Paragraph
                        as={ `span` }
                        className={ `flex items-center` }
                        variant={ `secondary` }
                      >
                        <ForwardToInboxIcon
                          className={ `mr-1.5` }
                          fontSize={ `small` }
                        />
                        { profile.bccAddress }

                      </Paragraph>
                    </span>
                  </Tooltip>
                </div>
              ) }
            </When>

            <When condition={ !!profile.ccAddress }>
              { () => (
                <div>
                  <Tooltip title={ `CC Address` }>
                    <span>
                      <Paragraph
                        as={ `span` }
                        className={ `flex items-center` }
                        variant={ `secondary` }
                      >
                        <OutgoingMailIcon
                          className={ `mr-1.5` }
                          fontSize={ `small` }
                        />
                        { profile.ccAddress }
                      </Paragraph>
                    </span>
                  </Tooltip>
                </div>
              ) }
            </When>

            <When condition={ !!profile.assigneeRecipient }>
              { () => (
                <div>
                  <Tooltip title={ `The invoice or contact assignee is ${profile.assigneeRecipient === SenderProfileAssigneeRecipient.CC ? `CC'd` : `BCC'd`}.` }>
                    <span>
                      <Paragraph
                        as={ `span` }
                        className={ `flex items-center` }
                        variant={ `secondary` }
                      >
                        <PersonIcon
                          className={ `mr-1.5` }
                          fontSize={ `small` }
                        />
                        { profile.assigneeRecipient === SenderProfileAssigneeRecipient.CC ? `CC assignee` : `BCC assignee` }
                      </Paragraph>
                    </span>
                  </Tooltip>
                </div>
              ) }
            </When>

            <When condition={ !!profile.hideLogo || !!profile.logoUrl }>
              { () => (
                <div>
                  <Tooltip title={ profile.hideLogo ? `Emails from this profile are sent without a logo.` : `This profile has a custom logo.` }>
                    <span>
                      <Paragraph
                        as={ `span` }
                        className={ `flex items-center` }
                        variant={ `secondary` }
                      >
                        { profile.hideLogo ?
                          <HideImageIcon
                            className={ `mr-1.5` }
                            fontSize={ `small` }
                          /> :
                          <ImageIcon
                            className={ `mr-1.5` }
                            fontSize={ `small` }
                          /> }
                        { profile.hideLogo ? `No logo` : `Has custom logo` }
                      </Paragraph>
                    </span>
                  </Tooltip>
                </div>
              ) }
            </When>

            <When condition={ !!profile.signatureHtml }>
              { () => (
                <div>
                  <Tooltip title={ `This profile has a custom signature.` }>
                    <span>
                      <Paragraph
                        as={ `span` }
                        className={ `flex items-center` }
                        variant={ `secondary` }
                      >
                        <FaxIcon
                          className={ `mr-1.5` }
                          fontSize={ `small` }
                        />
                        { `Has custom signature` }
                      </Paragraph>
                    </span>
                  </Tooltip>
                </div>
              ) }
            </When>
          </div>
        </div>

        <button
          className={ `text-sm text-blue-600 hover:underline` }
          onClick={ () => setIsEditing(true) }
        >
          { `Edit` }
        </button>
      </div>
    </Card>
  );
}
